/* eslint-disable no-console */
import {
  DestinationEntityRecordStatus,
  DestinationEntityStatus,
  IDestinationEntity,
  IDestinationEntityRecord,
  IPracticeMigration,
  ISourceEntityRecord,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  getDestinationEntityRecordCount,
  getDestinationEntityRecordsByUid,
  getLastUpdatedDestinationEntityRecords,
  searchDestinationEntityRecords,
} from '@principle-theorem/principle-db-connector';
import {
  Firestore,
  WithPrimaryKey,
  getEnumValues,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { DataConnect } from 'firebase/data-connect';
import { first } from 'lodash';
import { DestinationEntityRecord } from '../destination-entity-record/destination-entity-record.browser';
import { PracticeMigration } from '../../practice-migrations';

export class DestinationEntity {
  static init(
    overrides: Pick<IDestinationEntity, 'metadata'>
  ): IDestinationEntity {
    return {
      uid: overrides.metadata.key,
      status: DestinationEntityStatus.Uninitialised,
      ...overrides,
    };
  }

  static withMetadataDescription(
    destinationEntity: IDestinationEntity,
    description: string
  ): IDestinationEntity {
    return {
      ...destinationEntity,
      metadata: { ...destinationEntity.metadata, description },
    };
  }

  static async records<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(data: {
    migration: WithRef<IPracticeMigration>;
    destinationEntity: WithRef<IDestinationEntity>;
    statuses: DestinationEntityRecordStatus[];
    limit?: number;
    offset?: number;
    orderBy?: {
      key: string;
      direction: 'asc' | 'desc';
    };
  }): Promise<
    WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>[]
  > {
    const dataConnect = await PracticeMigration.dataConnect(data.migration);
    const results = await searchDestinationEntityRecords(dataConnect, {
      destinationType: data.destinationEntity.metadata.key,
      statuses: data.statuses,
      limit: data.limit,
      offset: data.offset,
      // orderBy: data.orderBy,
    });

    const unserialisedData = results.data.destinationEntityRecords.map(
      (record) =>
        DestinationEntityRecord.unserialise<Data, FailData, SourceKey>(record)
    );
    return unserialisedData;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  static async resolveSourceRecords<
    Source extends ISourceEntityTypes = ISourceEntityTypes,
  >(_data: {
    migration: WithRef<IPracticeMigration>;
    destinationEntity: WithRef<IDestinationEntity>;
    statuses: DestinationEntityRecordStatus[];
    limit?: number;
    offset?: number;
    orderBy?: {
      key: string;
      direction: 'asc' | 'desc';
    };
  }): Promise<WithPrimaryKey<ISourceEntityRecord<Source>>[]> {
    throw new Error('Not implemented');
  }

  static async getRecord<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    uid: string
  ): Promise<
    | WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>
    | undefined
  > {
    const dataConnect = await PracticeMigration.dataConnect(migration);
    try {
      const results = await getDestinationEntityRecordsByUid(dataConnect, {
        destinationType: destinationEntity.metadata.key,
        uid,
      });

      const record = first(results.data.destinationEntityRecords);
      if (!record) {
        return;
      }

      return DestinationEntityRecord.unserialise<Data, FailData, SourceKey>(
        record
      );
    } catch (error) {
      console.error(`DestinationEntity.getDestinationRecordEntityByUid`, error);
      throw error;
    }
  }

  static async getLastDestinationRecordEntity<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(
    dataConnect: DataConnect,
    destinationEntity: WithRef<IDestinationEntity>
  ): Promise<
    | WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>
    | undefined
  > {
    try {
      const results = await getLastUpdatedDestinationEntityRecords(
        dataConnect,
        {
          destinationType: destinationEntity.metadata.key,
        }
      );
      const record = first(results.data.destinationEntityRecords);

      if (!record) {
        return;
      }

      return DestinationEntityRecord.unserialise<Data, FailData, SourceKey>(
        record
      );
    } catch (error) {
      console.error(`DestinationEntity.getLastDestinationRecordEntity`, error);
      throw error;
    }
  }

  static async getDestinationRecordEntityCount(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    statuses: DestinationEntityRecordStatus[] = getEnumValues(
      DestinationEntityRecordStatus
    )
  ): Promise<number> {
    const dataConnect = await PracticeMigration.dataConnect(migration);
    try {
      const results = await getDestinationEntityRecordCount(dataConnect, {
        destinationType: destinationEntity.metadata.key,
        statuses,
      });
      return results.data.destinationEntityRecords.length;
    } catch (error) {
      console.error(`DestinationEntity.getDestinationRecordEntityCount`, error);
      throw error;
    }
  }

  static async setLastRunAt(
    destinationEntity: WithRef<IDestinationEntity>
  ): Promise<void> {
    await Firestore.patchDoc(destinationEntity.ref, {
      lastRunAt: toTimestamp(),
    });
  }
}

export function syncDestinationSize(
  _entity: WithRef<IDestinationEntity>,
  _migration: WithRef<IPracticeMigration>
): Promise<{
  size: number;
  failedSize: number;
  mergeConflictSize: number;
  missingCustomMappingSize: number;
}> {
  throw new Error('Not implemented');
}
